import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { compressImage } from '../utils/imageUpload';

export const TITLES = [
  'Firefighter',
  'Firefighter/EMT',
  'Firefighter/Paramedic',
  'EMT',
  'Paramedic',
  'LT Firefighter',
  'LT Paramedic',
  'Captain',
  'Operations Chief',
  'Battalion Chief',
  'Fire Chief',
  'Volunteer',
  'Reserve',
];

export const SHIFTS = ['A', 'B', 'C', 'D'];

export const CERTIFICATIONS = [
  'EMT-B',
  'AEMT',
  'Paramedic',
  'Hazmat Awareness',
  'Hazmat Technician',
  'Fire Officer I',
  'Fire Officer II',
  'Fire Instructor I',
  'Fire Instructor II',
  'Rope Rescue',
  'Confined Space',
  'Swiftwater Rescue',
  'Wildland FFT2',
  'Wildland FFT1',
  'NIMS 100',
  'NIMS 200',
  'NIMS 700',
  'NIMS 800',
  'CPR Instructor',
  'ACLS',
  'PALS',
];

// Returns the user's profile data from Firestore and functions to update it.
export function useUserProfile(user) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setProfile(null); setLoading(false); return; }
    return onSnapshot(doc(db, 'users', user.uid),
      (snap) => {
        setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error('[useUserProfile] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [user]);

  const updateProfile = useCallback(async (updates) => {
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), updates);
  }, [user]);

  const uploadPhoto = useCallback(async (file) => {
    if (!user || !file) return null;
    if (!file.type?.startsWith('image/')) throw new Error('Please choose an image file');
    // Re-encode to strip EXIF/GPS metadata and shrink before upload.
    const blob = await compressImage(file, { maxDim: 512 });
    const sRef = ref(storage, `profilePictures/${user.uid}`);
    await uploadBytes(sRef, blob, { contentType: 'image/jpeg' });
    const url = await getDownloadURL(sRef);
    await updateDoc(doc(db, 'users', user.uid), { photoUrl: url });
    return url;
  }, [user]);

  return { profile, loading, updateProfile, uploadPhoto };
}
